import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Chip, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import TaskView from './TaskView';

import moment from 'moment';

const TaskOverview = (props) => {
  const [activeFilter, setActiveFilter] = React.useState('all');
  const caregiverTasks = props.caregiverTasks;
  const caregiverBackend = props.caregiverBackend;
  const playbooks = props.playbooks;
  const filters = React.useMemo(() => {
    const baseFilters = [
      {key: 'all', title: 'caregiver.CaregiverDetail.Tasks.Filters.All', isTranslated: true},
      {key: 'current', title: 'caregiver.CaregiverDetail.Tasks.Sections.Current', isTranslated: true},
      {key: 'next', title: 'caregiver.CaregiverDetail.Tasks.Sections.Next', isTranslated: true},
      {key: 'overdue', title: 'caregiver.CaregiverDetail.Tasks.Sections.Overdue', isTranslated: true},
      {key: 'unassigned', title: 'caregiver.CaregiverDetail.Tasks.Sections.Unassigned', isTranslated: true},
    ];
    const seenPlaybooks = new Set();

    caregiverTasks.forEach((task) => {
      if (!/^RN:PLAYBOOK-V1-[^:]+$/.test(task.playbookRn) || seenPlaybooks.has(task.playbookRn)) {
        return;
      }

      const playbook = playbooks.find((currentPlaybook) => currentPlaybook.playbookRn === task.playbookRn);

      if (!playbook) {
        return;
      }

      seenPlaybooks.add(task.playbookRn);
      baseFilters.push({
        key: task.playbookRn,
        title: `/${playbook.title}`,
        isTranslated: false,
      });
    });

    return baseFilters;
  }, [caregiverTasks, playbooks]);

  const groupedTasks = React.useMemo(() => {
    const now = moment();
    const nowTime = now.valueOf();
    const dayEnd = now.clone().endOf('day').valueOf();

    const openTasks = caregiverTasks.filter((task) => task.status !== 'COMPLETE');

    return openTasks.reduce((result, task) => {
      if (task.ownerRns?.length === 0) {
        result.unassigned.push(task);
      }
      const dueTime = moment(task.dueDate).valueOf();

      if (dueTime < nowTime) {
        result.overdue.push(task);
      } else if (dueTime <= dayEnd) {
        result.current.push(task);
      } else if (dueTime > dayEnd) {
        result.next.push(task);
      }

      return result;
    }, {
      current: [],
      next: [],
      overdue: [],
      unassigned: [],
    });
  }, [caregiverTasks]);

  const sections = React.useMemo(() => {
    return [
      {
        key: 'current',
        titleKey: 'caregiver.CaregiverDetail.Tasks.Sections.Current',
        tasks: groupedTasks.current,
      },
      {
        key: 'next',
        titleKey: 'caregiver.CaregiverDetail.Tasks.Sections.Next',
        tasks: groupedTasks.next,
      },
      {
        key: 'overdue',
        titleKey: 'caregiver.CaregiverDetail.Tasks.Sections.Overdue',
        tasks: groupedTasks.overdue,
      },
      {
        key: 'unassigned',
        titleKey: 'caregiver.CaregiverDetail.Tasks.Sections.Unassigned',
        tasks: groupedTasks.unassigned.sort((firstTask, secondTask) => {
          const firstDue = moment(firstTask.dueDate).valueOf();
          const secondDue = moment(secondTask.dueDate).valueOf();

          return firstDue - secondDue;
        }),
      },
    ];
  }, [groupedTasks]);

  const visibleSections = React.useMemo(() => {
    if (activeFilter === 'all') {
      return sections
          .filter((section) => section.key === 'current' || section.key === 'next')
          .map((section) => {
            if (section.key !== 'next') {
              return section;
            }

            return {
              ...section,
              tasks: section.tasks.slice(0, 2),
            };
          })
          .filter((section) => section.tasks.length > 0);
    }

    if (activeFilter.startsWith('RN:PLAYBOOK-V1-')) {
      return sections
          .map((section) => ({
            ...section,
            tasks: section.tasks.filter((task) => task.playbookRn === activeFilter),
          }))
          .filter((section) => section.tasks.length > 0);
    }

    return sections.filter((section) => section.key === activeFilter && section.tasks.length > 0);
  }, [activeFilter, sections]);

  const handleSetStatusToComplete = (taskRn, isComplete) => {
    if (isComplete) {
      return;
    }

    caregiverBackend.tasks.updateTaskStatus(taskRn, 'COMPLETE');
  };
  const removeTask = (taskData) => {
    caregiverBackend.tasks.removeTask(taskData.taskRn, () => undefined);
  };

  return (
    <Stack spacing={2}>
      <Typography
        variant='subtitle2'
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#6B6760',
        }}
      >
        <Translate expression='caregiver.CaregiverDetail.Tasks.OverviewTitle' />
      </Typography>
      <Stack
        direction='row'
        gap={1}
        sx={{
          flexWrap: 'wrap',
        }}
      >
        {
          filters.map((filter) => {
            const isActive = activeFilter === filter.key;

            return (
              <Chip
                key={filter.key}
                component='button'
                size='small'
                onClick={() => setActiveFilter(filter.key)}
                label={filter.isTranslated ? <Translate expression={filter.title} /> : filter.title}
                sx={{
                  backgroundColor: isActive ? '#FFF7F4' : '#F7F4EF',
                  border: '1px solid',
                  borderColor: isActive ? '#EBBDAD' : '#E8E4DE',
                  fontFamily: '"Roboto Mono", monospace',
                  color: isActive ? '#DA7955' : '#6B6760',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              />
            );
          })
        }
      </Stack>
      {
        visibleSections.length === 0 &&
          <Typography
            variant='body2'
            sx={{
              color: '#6B6760',
              fontSize: '12px',
            }}
          >
            <Translate expression='tasks.AllTasks.Empty' />
          </Typography>
      }
      {
        visibleSections.map((section) => {
          const sectionTasks = section.tasks;
          return (
            <Stack key={section.key} spacing={1.5}>
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 700,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#6B6760',
                }}
              >
                <Translate expression={section.titleKey} />
              </Typography>
              {
                sectionTasks.map((task) => (
                  <Box
                    key={task.taskRn}
                    sx={{
                      border: '1px solid #E8E4DE',
                      borderRadius: '10px',
                      overflow: 'hidden',
                    }}
                  >
                    <TaskView
                      task={task}
                      handleSetStatusToComplete={handleSetStatusToComplete}
                      removeTask={removeTask}
                      hideEditAction
                    />
                  </Box>
                ))
              }
            </Stack>
          );
        })
      }
    </Stack>
  );
};

TaskOverview.propTypes = {
  caregiverTasks: PropTypes.array.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  playbooks: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskOverview));
